/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSmartLinkContext } from '@atlaskit/link-provider';
import { CardDisplay, SmartLinkPosition, SmartLinkSize } from '../../../constants';
import { useSmartLinkAnalytics } from '../../../state/analytics';
import { getExtensionKey, getServices } from '../../../state/helpers';
import { isSpecialEvent } from '../../../utils';
import { flexibleUiOptions, titleBlockCss } from '../styled';
import { getMetadata } from '../utils';
import HoverCardLoadingView from './views/resolving';
import HoverCardUnauthorisedView from './views/unauthorised';
import HoverCardResolvedView from './views/resolved';
import { fireLinkClickedEvent } from '../../../utils/analytics/click';
import { useSmartCardState } from '../../../state/store';
import HoverCardForbiddenView from './views/forbidden';
import ContentContainer from './ContentContainer';
import { getIsAISummaryEnabled } from '../../../utils/ai-summary';
export var hoverCardClassName = 'smart-links-hover-preview';
var HoverCardContent = function HoverCardContent(_ref) {
  var _linkState$status, _cardState$details;
  var _ref$id = _ref.id,
    id = _ref$id === void 0 ? '' : _ref$id,
    _analytics = _ref.analytics,
    cardState = _ref.cardState,
    onActionClick = _ref.onActionClick,
    onResolve = _ref.onResolve,
    renderers = _ref.renderers,
    url = _ref.url,
    onMouseEnter = _ref.onMouseEnter,
    onMouseLeave = _ref.onMouseLeave,
    actionOptions = _ref.actionOptions;
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var defaultAnalytics = useSmartLinkAnalytics(url, undefined, id);
  var analytics = _analytics !== null && _analytics !== void 0 ? _analytics : defaultAnalytics;
  var extensionKey = useMemo(function () {
    return getExtensionKey(cardState.details);
  }, [cardState.details]);
  var linkState = useSmartCardState(url);
  var linkStatus = (_linkState$status = linkState.status) !== null && _linkState$status !== void 0 ? _linkState$status : 'pending';
  var _useSmartLinkContext = useSmartLinkContext(),
    isAdminHubAIEnabled = _useSmartLinkContext.isAdminHubAIEnabled;
  var isAISummaryEnabled = getIsAISummaryEnabled(isAdminHubAIEnabled, cardState.details);
  var services = getServices(linkState.details);
  var statusRef = useRef(linkStatus);
  var analyticsRef = useRef(analytics);
  useEffect(function () {
    /**
     * Must access current analytics object value via ref because its not stable
     * and it can trigger useEffect to re-run below
     */
    if (analyticsRef.current !== analytics) {
      analyticsRef.current = analytics;
    }
    if (statusRef.current !== linkStatus) {
      statusRef.current = linkStatus;
    }
  }, [analytics, linkStatus]);
  useEffect(function () {
    var previewDisplay = 'card';
    var previewInvokeMethod = 'mouse_hover';
    var cardOpenTime = Date.now();
    analyticsRef.current.ui.hoverCardViewedEvent({
      previewDisplay: previewDisplay,
      previewInvokeMethod: previewInvokeMethod,
      status: statusRef.current
    });
    return function () {
      var hoverTime = Date.now() - cardOpenTime;
      analyticsRef.current.ui.hoverCardDismissedEvent({
        previewDisplay: previewDisplay,
        previewInvokeMethod: previewInvokeMethod,
        hoverTime: hoverTime,
        status: statusRef.current
      });
    };
  }, []);
  var onClick = useCallback(function (event) {
    var isModifierKeyPressed = isSpecialEvent(event);
    analytics.ui.cardClickedEvent({
      id: id,
      display: CardDisplay.HoverCardPreview,
      status: cardState.status,
      isModifierKeyPressed: isModifierKeyPressed,
      actionSubjectId: 'titleGoToLink'
    });
    fireLinkClickedEvent(createAnalyticsEvent)(event);
  }, [createAnalyticsEvent, cardState.status, analytics.ui, id]);
  var data = (_cardState$details = cardState.details) === null || _cardState$details === void 0 ? void 0 : _cardState$details.data;
  var _getMetadata = getMetadata(extensionKey, data),
    subtitle = _getMetadata.subtitle;
  var titleMaxLines = subtitle && subtitle.length > 0 ? 1 : 2;
  var titleBlockProps = {
    maxLines: titleMaxLines,
    overrideCss: titleBlockCss,
    size: SmartLinkSize.Large,
    position: SmartLinkPosition.Center,
    subtitle: subtitle
  };
  var flexibleCardProps = {
    appearance: CardDisplay.HoverCardPreview,
    cardState: cardState,
    onClick: onClick,
    onResolve: onResolve,
    renderers: renderers,
    actionOptions: actionOptions,
    ui: flexibleUiOptions,
    url: url,
    children: null,
    analytics: analytics
  };
  var onClickStopPropagation = useCallback(function (e) {
    return e.stopPropagation();
  }, []);
  var getCardView = function getCardView(cardState) {
    if (cardState.metadataStatus === 'pending') {
      return jsx(HoverCardLoadingView, {
        flexibleCardProps: flexibleCardProps,
        titleBlockProps: titleBlockProps
      });
    }
    if (cardState.status === 'unauthorized' && services !== null && services !== void 0 && services.length) {
      return jsx(HoverCardUnauthorisedView, {
        analytics: analytics,
        extensionKey: extensionKey,
        id: id,
        flexibleCardProps: flexibleCardProps,
        url: url
      });
    }
    if (cardState.status === 'forbidden' || cardState.status === 'not_found') {
      return jsx(HoverCardForbiddenView, {
        flexibleCardProps: flexibleCardProps
      });
    }
    if (cardState.status === 'resolved') {
      return jsx(HoverCardResolvedView, {
        analytics: analytics,
        cardState: cardState,
        extensionKey: extensionKey,
        flexibleCardProps: flexibleCardProps,
        isAISummaryEnabled: isAISummaryEnabled,
        onActionClick: onActionClick,
        titleBlockProps: titleBlockProps,
        url: url
      });
    }
    return null;
  };
  var cardView = getCardView(cardState);
  return cardView ? jsx(ContentContainer, {
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    onClick: onClickStopPropagation,
    isAIEnabled: isAISummaryEnabled,
    url: url
  }, cardView) : null;
};
export default HoverCardContent;